// @flow
import * as React from 'react';

import { Plus } from 'lucide-react';
import { useDispatch } from 'react-redux';

import { View } from 'reshaped';

import { selectIsSamlUser } from '@gandi/authentication';
import {
  fetchCartOwnerList,
  selectCartOwnerList,
  selectCartUUID,
  selectIsLoadedCartOwnerList,
  selectIsFetchingCartOwnerList,
  selectIsFailureCartOwnerList,
  selectHasOwnerOnly,
} from '@gandi/cart';
import { selectCurrentId } from '@gandi/redux-sharing';
import { Link } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import {
  customersResource,
  fetchCustomersList,
} from 'src/Cart/Customer/modules/customers';
import { useCartSummaryContext } from 'src/Layout/context/CartSummary';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { SearchField } from '../../SearchField';
import { SelectContactForm } from '../../SelectContactForm';
import { useApplyInvoicedOrga } from '../../hooks/useApplyInvoicedOrga';
import type { Props } from '../../hooks/useModalType';
import { useSearchField } from '../../hooks/useSearchField';

export function InvoiceSelectModal({
  onClose,
  openModal,
  currentRadioValue,
  setCurrentRadioValue,
}: Props): React.MixedElement {
  const dispatch = useDispatch();
  const { __ } = useTranslator();

  const invoicesContact = useAppSelector(selectCartOwnerList);

  const { filteredList, searchTerm, handleFilter } = useSearchField(
    invoicesContact,
    ['name', 'orgname', 'firstname', 'lastname'],
  );

  const cartId = useAppSelector(selectCartUUID);

  const { cartOwnerOrganization, updateSharingIdContext } =
    useCartSummaryContext();

  const isFetchingCartOwners = useAppSelector(selectIsFetchingCartOwnerList);
  const isLoadedCartOwners = useAppSelector(selectIsLoadedCartOwnerList);
  const isFailureCartOwners = useAppSelector(selectIsFailureCartOwnerList);

  const isSamlUser = useAppSelector(selectIsSamlUser);
  const hasOwnerOnly = useAppSelector(selectHasOwnerOnly);

  React.useEffect(() => {
    // TODO: use new resource
    if (!isFetchingCartOwners && !isLoadedCartOwners && !isFailureCartOwners) {
      dispatch(fetchCartOwnerList(cartId));
    }
  }, [
    cartId,
    dispatch,
    isFetchingCartOwners,
    isLoadedCartOwners,
    isFailureCartOwners,
  ]);

  const sharingId = useAppSelector(selectCurrentId);
  const shouldFetch = useAppSelector(state =>
    customersResource.selectShouldFetch(state, sharingId),
  );

  React.useEffect(() => {
    if (shouldFetch && cartOwnerOrganization?.reseller) {
      dispatch(fetchCustomersList(cartOwnerOrganization.id));
    }
  }, [shouldFetch, cartOwnerOrganization, dispatch]);

  const handleOwnerChange = useApplyInvoicedOrga({
    onSuccess: () => {
      setCurrentRadioValue();
      onClose();
    },
    updateSharingIdContext,
  });

  const radioValue = cartOwnerOrganization?.id;

  function handleEdit(value: string) {
    setCurrentRadioValue(value);
    openModal({
      type: 'editAccountModal',
      orgId: value,
      actionOnSuccess: 'onBack',
    });
  }

  function handleCreateContact() {
    openModal({
      type: 'createAccountModal',
      actionOnSuccess: 'onBack',
      currentRole: 'BILLING',
    });
  }

  const showFilter = invoicesContact.length > 4;

  return (
    <>
      {showFilter && <SearchField value={searchTerm} onChange={handleFilter} />}
      {!hasOwnerOnly && !isSamlUser && (
        <View
          as="div"
          attributes={{ style: { marginBottom: 'var(--rs-unit-x1)' } }}
        >
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link icon={Plus} onClick={handleCreateContact}>
            {__('Create contact')}
          </Link>
        </View>
      )}

      <SelectContactForm
        type="organization"
        initialValue={currentRadioValue || radioValue}
        items={filteredList}
        handleEdit={handleEdit}
        handleSubmit={handleOwnerChange}
        openModal={openModal}
        checkMissingVAT
        checkMissingInfo
      />
    </>
  );
}
